import * as React from 'react'
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type CalloutVariant = 'info' | 'warning' | 'success' | 'danger'

/* Tint only — never a solid fill (a solid warm fill always means a primary
   action). The icon plus a sentence, not hue, carries the meaning. */
const VARIANTS: Record<CalloutVariant, { tint: string; icon: LucideIcon }> = {
  info: { tint: 'bg-info-tint text-info-tint-foreground', icon: InfoIcon },
  warning: { tint: 'bg-warning-tint text-warning-tint-foreground', icon: TriangleAlertIcon },
  success: { tint: 'bg-success-tint text-success-tint-foreground', icon: CircleCheckIcon },
  danger: { tint: 'bg-destructive-tint text-destructive-tint-foreground', icon: CircleAlertIcon },
}

export interface CalloutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CalloutVariant
  /** Optional bold lead line above the sentence */
  title?: React.ReactNode
  /** Overrides the variant's icon (e.g. CloudOff for the offline banner) */
  icon?: LucideIcon
  /** Buttons or controls, laid out under the message */
  action?: React.ReactNode
}

/**
 * The one inline message surface: info, warning, success, danger. Callers
 * that announce something on change (errors, connectivity) pass role="alert";
 * static guidance takes no role.
 */
const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(
  ({ variant = 'info', title, icon, action, className, children, ...props }, ref) => {
    const { tint, icon: VariantIcon } = VARIANTS[variant]
    const Icon = icon ?? VariantIcon
    return (
      <div ref={ref} className={cn('rounded-3xl p-4', tint, className)} {...props}>
        <div className="flex items-start gap-2.5">
          <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none" />
          <div className="min-w-0 flex-1 text-[13px] leading-relaxed">
            {title && <p className="mb-0.5 text-sm font-semibold">{title}</p>}
            {children}
          </div>
        </div>
        {action && <div className="mt-3">{action}</div>}
      </div>
    )
  }
)
Callout.displayName = 'Callout'

export { Callout }
